import { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { fetchMe, logout } from "@/lib/api";
import { LogOut, FileText, Calendar, ShieldCheck } from "lucide-react";

export default function Dashboard() {
  const location = useLocation();
  const navigate = useNavigate();
  const [user, setUser] = useState(location.state?.user || null);
  const [loading, setLoading] = useState(!location.state?.user);

  // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
  const handleLogin = () => {
    const redirectUrl = window.location.origin + "/dashboard";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  useEffect(() => {
    if (user) return;
    if (window.location.hash?.includes("session_id=")) {
      setLoading(false);
      return;
    }
    fetchMe()
      .then((u) => {
        setUser(u);
      })
      .catch(() => setUser(null))
      .finally(() => setLoading(false));
    // Intentionally runs only when `user` changes; fetchMe and setters are stable.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  const handleLogout = async () => {
    await logout().catch(() => {});
    setUser(null);
    navigate("/");
  };

  if (loading) {
    return (
      <div className="container-x py-32 text-center text-slate-500" data-testid="dashboard-loading">
        Loading…
      </div>
    );
  }

  if (!user) {
    return (
      <section className="bg-white py-24 md:py-32" data-testid="dashboard-login">
        <div className="container-x max-w-xl text-center">
          <div className="w-14 h-14 rounded-2xl bg-[#7C3AED]/10 text-[#7C3AED] grid place-items-center mx-auto mb-6">
            <ShieldCheck className="w-7 h-7" />
          </div>
          <div className="overline mb-4">Customer portal</div>
          <h1 className="font-display text-4xl font-extrabold text-[#0F172A] leading-tight">
            Access your assessment reports.
          </h1>
          <p className="text-slate-600 mt-5 leading-relaxed">
            Sign in with Google to access your reports, scheduling history, and document downloads.
          </p>
          <button
            data-testid="dashboard-login-button"
            onClick={handleLogin}
            className="btn-primary mt-10 mx-auto"
          >
            Sign in with Google
          </button>
        </div>
      </section>
    );
  }

  return (
    <div data-testid="dashboard-page">
      <section className="bg-[#0F172A] text-white py-16">
        <div className="container-x flex items-center justify-between flex-wrap gap-6">
          <div className="flex items-center gap-4">
            {user.picture && (
              <img src={user.picture} alt={user.name} className="w-14 h-14 rounded-full border-2 border-white/20" />
            )}
            <div>
              <div className="text-white/60 text-xs uppercase tracking-wider">Welcome back</div>
              <div className="font-display text-2xl font-bold mt-1">{user.name}</div>
              <div className="text-white/60 text-sm">{user.email}</div>
            </div>
          </div>
          <button
            data-testid="dashboard-logout-button"
            onClick={handleLogout}
            className="btn-outline-dark text-sm"
          >
            <LogOut className="w-4 h-4 mr-2" /> Sign out
          </button>
        </div>
      </section>

      <section className="section-pad bg-white">
        <div className="container-x grid md:grid-cols-3 gap-6">
          <div className="card-soft p-8">
            <FileText className="w-7 h-7 text-[#7C3AED] mb-4" />
            <h3 className="font-display font-bold text-lg text-[#0F172A]">My Reports</h3>
            <p className="text-sm text-slate-600 mt-3 leading-relaxed">
              Your environmental assessment reports will appear here as they are completed.
            </p>
            <div className="mt-6 text-xs text-slate-400 uppercase tracking-wider">No reports yet</div>
          </div>
          <div className="card-soft p-8">
            <Calendar className="w-7 h-7 text-[#7C3AED] mb-4" />
            <h3 className="font-display font-bold text-lg text-[#0F172A]">My Schedule</h3>
            <p className="text-sm text-slate-600 mt-3 leading-relaxed">
              Upcoming assessments and inspection dates.
            </p>
            <div className="mt-6 text-xs text-slate-400 uppercase tracking-wider">No scheduled visits</div>
          </div>
          <div className="card-soft p-8">
            <ShieldCheck className="w-7 h-7 text-[#7C3AED] mb-4" />
            <h3 className="font-display font-bold text-lg text-[#0F172A]">Account</h3>
            <p className="text-sm text-slate-600 mt-3 leading-relaxed">
              Authenticated via Google. Your session is secure for 7 days.
            </p>
          </div>
        </div>
      </section>
    </div>
  );
}
